import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Link from '@material-ui/core/Link';
import { Link as RouterLink } from 'react-router-dom';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import Button from '..//Button';
import Typography from '@material-ui/core/Typography';
import { LocationInput } from '../input';
import Alert from '@material-ui/lab/Alert';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import InputLabel from '@material-ui/core/InputLabel';
import { webToLead } from '../../api/provider';
import RecaptchaProvider from '../RecaptchaProvider';
import Recaptcha from 'react-recaptcha';
import config from '../../config';
import ThumbUpOutlinedIcon from '@material-ui/icons/ThumbUpOutlined';
import { connect } from 'react-redux';
import * as EmailValidator from 'email-validator';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import * as gtm from '../../lib/gtm';

// product_type *must* be set to one of these for the lead to be visible.
/*
  'Clickability Basic Listing',
  'Clickability Premium Listing',
  'Clickability Brokerage',
  'Clickability Other',
*/

const PRODUCT_TYPE_BY_INTEREST = {
  'ndis_services': 'Clickability Brokerage',
  'provider_accreditation': 'Clickability Premium Listing',
  'general_enquiries': 'Clickability Other',
  'feedback': 'Clickability Other',
};

export const LEAD_CHANNEL = {
  PROVIDER_SIGNUP: 'Web - Provider Sign Up',
  CONTACT_US: 'Web - Contact Clickability',
  PARTICIPANT_MATCH: 'Web - Participant Match',
};

const DEFAULT_PRODUCT_TYPE = 'Clickability Other';  // safe value if no other value is set

const formatDescription = (data) => {
  const colWidth = 20;
  return Object.entries(data).map(([k, v]) => (k + ':').padEnd(colWidth, ' ') + v).join('\n');
};

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
  },
  formControl: {
    width: '100%',
    margin: '1rem 0',
  },
  button: {
    margin: '1rem 0',
  },
  submitted: {
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  icon: {
    color: theme.palette.common.tealish,
    marginRight: '1rem',
  }
}));

const RequestForm = ({formName, isAuthenticated, user, leadChannel}) => {
  const classes = useStyles();
  const userName = isAuthenticated ? user.username : '';
  const userUuid = isAuthenticated ? user.user_uuid : '';
  const userEmail = isAuthenticated ? user.email : '';
  const [loading, setLoading] = useState(true);
  const [verifyCode, setVerifyCode] = useState();
  const [errors, setErrors] = useState({});
  const [emailOptIn, setEmailOptIn] = useState(true);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [formValid, setFormValid] = useState(true);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [error, setError] = useState(undefined);
  const [data, setData] = useState({
    first_name: '',
    last_name: '',
    email: userEmail,
    phone: '',
    location: '',
    interested_in: 'ndis_services',
    description: '',
    form_name: formName,
    user_uuid: userUuid,
    username: userName,
  });

  const verified = verifyCode !== undefined;

  const setFormField = (attribute, value) => {
    let copy = Object.assign({}, data);
    copy[attribute] = value;
    setData(copy);
  };

  const handleChange = (value, key) => {
    setFormField(key, value);
  };

  const validateForm = () => {
    const phoneNumber = parsePhoneNumberFromString(data.phone, 'AU');
    const errors = {
      email: data.email !== '' && !EmailValidator.validate(data.email) && 'That doesn\'t look like a valid email address.',
      firstName: data.first_name.length < 2 && 'First name must have at least 2 characters.',
      lastName: data.last_name.length < 2 && 'Last name must have at least 2 characters.',
      phone: data.phone !== '' && (!phoneNumber || !phoneNumber.isValid()) && 'That doesn\'t look like a valid phone number.',
      location: data.location === '' && 'Please select a location',
      description: (data.description.split(' ').length) < 5 && 'Description must be at least 5 words',
    };
    if (data.phone === '' && data.email === '') {
      errors.phone = errors.email = 'You must provide a valid email address OR phone number.';
    }
    setErrors(errors);
    const valid = !Object.values(errors).some(v => v);
    return valid;
  };

  const emailOptInHandler = () => {
    setEmailOptIn(!emailOptIn);
  };

  const postData = async () => {
    setLoading(true);
    const core_data = {
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email,
      phone: data.phone,
      email_opt_in: emailOptIn,
      lead_source: 'Web',
      lead_channel: leadChannel,
      lead_type: 'Participant / Member / Client',  // currently all forms are particiant lead forms.
      product_type: PRODUCT_TYPE_BY_INTEREST[data.interested_in] || DEFAULT_PRODUCT_TYPE,
    };

    const extra_data = {
      qualification_notes: formatDescription(data),
      verify_code: verifyCode,
    };

    try {
      await webToLead({...core_data, ...extra_data});
      gtm.sendEvent('submit', {formName});
      setFormSubmitted(true);
      setLoading(false);
    } catch (e) {
      setError('An error occurred sending your enquiry.  Please try again later or call us.');
      setLoading(false);
    }
  };

  const submit = () => {
    if (!validateForm()) {
      setFormValid(false);
    } else {
      setFormValid(true);
      postData();
    }
  };

  if (formSubmitted) {
    return (
      <div className={classes.submitted}>
        <ThumbUpOutlinedIcon className={classes.icon} fontSize="large"/>
        <Typography variant="h6" color="primary">
          <b>All done! </b>Our team will be in touch with you shortly.
        </Typography>
      </div>
    );
  }

  return (
    <RecaptchaProvider>
      <form className={classes.root}>

        <FormControl className={classes.formControl}>
          <TextField
            required
            error={!!errors.firstName}
            placeholder="First name"
            label="First name"
            variant="outlined"
            value={data.first_name}
            onChange={e => handleChange(e.target.value, 'first_name')}
          />
          {errors.firstName && <Alert severity="error">{errors.firstName}</Alert>}
        </FormControl>

        <FormControl className={classes.formControl}>
          <TextField
            required
            error={!!errors.lastName}
            placeholder="Last name"
            label="Last name"
            variant="outlined"
            value={data.last_name}
            onChange={e => handleChange(e.target.value, 'last_name')}
          />
          {errors.lastName && <Alert severity="error">{errors.lastName}</Alert>}
        </FormControl>

        <FormControl className={classes.formControl}>
          <TextField
            required
            error={!!errors.email}
            placeholder="Email"
            label="Email"
            variant="outlined"
            value={data.email}
            onChange={e => handleChange(e.target.value, 'email')}
          />
          {errors.email && <Alert severity="error">{errors.email}</Alert>}
        </FormControl>

        <FormControl className={classes.formControl}>
          <TextField
            required
            error={!!errors.phone}
            placeholder="Phone number"
            label="Phone number"
            variant="outlined"
            value={data.phone}
            onChange={e => handleChange(e.target.value, 'phone')}
          />
          {errors.phone && <Alert severity="error">{errors.phone}</Alert>}
        </FormControl>

        <FormControl className={classes.formControl}>
          <LocationInput
            required
            onEnter={e => handleChange(e, 'location')}
            value={data.location}
            error={!!errors.location}
          />
          {errors.location && <Alert severity="error">{errors.location}</Alert>}
        </FormControl>

        <FormControl variant="outlined" className={classes.formControl}>
          <InputLabel>I am interested in:</InputLabel>
          <Select
            value={data.interested_in}
            onChange={e => handleChange(e.target.value, 'interested_in')}
            label="I am interested in:"
          >
            <MenuItem value='ndis_services'>NDIS Services</MenuItem>
            <MenuItem value='provider_accreditation'>Provider Accreditation</MenuItem>
            <MenuItem value='general_enquiries'>General Enquiries</MenuItem>
            <MenuItem value='feedback'>Feedback</MenuItem>
          </Select>
        </FormControl>

        <FormControl className={classes.formControl}>
          <TextField
            required
            error={!!errors.description}
            placeholder="Describe your needs"
            label="Describe your needs"
            variant="outlined"
            multiline
            rows="5"
            value={data.description}
            onChange={e => handleChange(e.target.value, 'description')}
          />
          {errors.description && <Alert severity="error">{errors.description}</Alert>}
        </FormControl>

        <FormControl className={classes.formControl}>
          <FormControlLabel
            control={<Checkbox checked={emailOptIn} onChange={emailOptInHandler} />}
            label="Inform me about updates, features and offers!"
          />
          <FormControlLabel
            control={<Checkbox checked={termsAgreed} onChange={() => setTermsAgreed(!termsAgreed)} />}
            label={
              (
                <Typography variant="body2">
                  I agree to Clickability&apos;s&nbsp;
                  <Link component={RouterLink} to="/terms">
                    <b>Terms and Conditions</b>
                  </Link>
                  &nbsp;&&nbsp;
                  <Link component={RouterLink} to="/privacy">
                    <b>Privacy Policy.</b>
                  </Link>
                </Typography>
              )
            }
          />
          {errors.terms && <Alert severity="error">{errors.terms}</Alert>}
        </FormControl>

        <div className={classes.formControl}>
          <Recaptcha
            sitekey={config.settings.RECAPTCHA_KEY}
            render="explicit"
            onloadCallback={() => setLoading(false)}
            verifyCallback={(code) => setVerifyCode(code)}
            expiredCallback={() => setVerifyCode(undefined)}
          />
        </div>

        <Button
          variant="contained"
          onClick={submit}
          className={classes.button}
          label="Submit"
          color="primary"
          disabled={loading || !verified || !termsAgreed}
          busy={loading}
        />
        {!formValid && <Alert severity="error">Some information you entered is not valid. Please update the fields highlighted in red and try again.</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
      </form>
    </RecaptchaProvider>
  );

};

RequestForm.propTypes = {
  formName: PropTypes.string.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  user: PropTypes.object,
  leadChannel: PropTypes.string.isRequired,
};

const mapStateToProps = state => {
  return {
    isAuthenticated: state.auth.isAuthenticated,
    user: state.auth.profile,
  };
};

export default connect(mapStateToProps)(RequestForm);
